import fetch from "auth/FetchInterceptor";
import JwtAuthService from "services/JwtAuthService";

const PING_INTERVAL_MS = 60 * 1000;
const LOGIN_WATCH_MS = 4 * 1000;
// dedup cross-aba: no máximo 1 "view" (abertura) por minuto por navegador.
// localStorage é compartilhado entre abas da mesma origem.
const VIEW_THROTTLE_MS = 60 * 1000;
const LAST_VIEW_KEY = "treino_presenca_ultimo_view";
// Marcador do User-Agent injetado pela WebView do app do profissional (mesmo
// do hook useIsAppWebView). Quando presente, a sessão roda dentro do app pro.
const APP_PROF_UA_MARKER = "Treinoio-Profissional-App";

const PresenceService = {};

// 'app_prof' quando a sessão roda dentro da WebView do app do profissional;
// senão 'web' (desktop/navegador). Separa o uso do app pro do web nas métricas.
function plataformaAtual() {
	try {
		if (
			typeof navigator !== "undefined" &&
			navigator.userAgent &&
			navigator.userAgent.includes(APP_PROF_UA_MARKER)
		) {
			return "app_prof";
		}
	} catch (e) {
		// se navigator falhar, cai pro padrão
	}
	return "web";
}

// Já houve uma abertura neste último minuto (em qualquer aba)? Se sim, reserva
// é false; senão grava o timestamp (compartilhado entre abas) e libera.
function viewRecente() {
	try {
		const ultimo = parseInt(localStorage.getItem(LAST_VIEW_KEY) || "0", 10);
		if (Date.now() - ultimo < VIEW_THROTTLE_MS) return true;
		localStorage.setItem(LAST_VIEW_KEY, String(Date.now()));
		return false;
	} catch (e) {
		return false; // se localStorage falhar, não bloqueia
	}
}

// Envia uma batida de presença; só quando logado. Path SEM /api (já está na
// baseURL = .../treino/api). Best-effort: o header no-notification silencia o
// toast global de erro em falha de rede. Um "view" repetido dentro do mesmo
// minuto (outra aba / alt-tab) vira "ping": atualiza presença e tempo, mas não
// conta abertura de novo.
async function enviarPresenca(tipo) {
	if (!JwtAuthService.isLogged()) return;
	let tipoFinal = tipo;
	if (tipo === "view" && viewRecente()) {
		tipoFinal = "ping";
	}
	try {
		await fetch({
			url: "/v1/rpc/presenca",
			method: "post",
			data: { plataforma: plataformaAtual(), tipo: tipoFinal },
			headers: { "no-notification": "true" },
		});
	} catch (e) {
		// presença nunca pode quebrar a UI
	}
}

// start() retorna uma função de cleanup para usar no return do useEffect.
PresenceService.start = function start() {
	let intervalId = null;
	let loginWatchId = null;
	// App é a raiz e monta na tela de login (deslogado); o login é por hash-route
	// (sem remount/reload), então observamos a borda deslogado -> logado para
	// contar a "view" de abertura da sessão.
	let logadoAntes = JwtAuthService.isLogged();

	const stopPing = () => {
		if (intervalId) {
			clearInterval(intervalId);
			intervalId = null;
		}
	};

	const startPing = () => {
		stopPing(); // garante que nunca há 2 intervalos rodando
		intervalId = setInterval(() => {
			if (document.visibilityState === "visible") {
				enviarPresenca("ping");
			}
		}, PING_INTERVAL_MS);
	};

	const onVisibilityChange = () => {
		if (document.visibilityState === "visible") {
			enviarPresenca("view");
			startPing();
		} else {
			stopPing(); // não pingar com a aba oculta
		}
	};

	// watcher de login: dispara a "view" quando o usuário acabou de logar
	loginWatchId = setInterval(() => {
		const logadoAgora = JwtAuthService.isLogged();
		if (
			logadoAgora &&
			!logadoAntes &&
			document.visibilityState === "visible"
		) {
			enviarPresenca("view");
		}
		logadoAntes = logadoAgora;
	}, LOGIN_WATCH_MS);

	// boot: se já está logado e visível (refresh / sessão salva), conta a abertura
	if (document.visibilityState === "visible") {
		enviarPresenca("view"); // no-op se não logado (gate isLogged)
		startPing();
	}

	document.addEventListener("visibilitychange", onVisibilityChange);

	return () => {
		document.removeEventListener("visibilitychange", onVisibilityChange);
		stopPing();
		if (loginWatchId) {
			clearInterval(loginWatchId);
			loginWatchId = null;
		}
	};
};

export default PresenceService;
